import { BillingCode } from 'src/participantHistory/intakeNote/billingCodeConstants'
import { z } from 'zod'
import { AccessibilityNeedsFormSchema } from './AccessibilityNeedsFormSchema'
import { ReasonForSeekingTreatmentFormSchema } from './ReasonForSeekingTreatmentFormSchema'
import { MentalHealthTreatmentHistoryFormSchema } from './MentalHealthTreatmentHistoryFormSchema'
import { RiskAssessmentFormSchema } from './RiskAssessmentFormSchema'
import { BiopsychosocialEvaluationFormSchema } from './BiopsychosocialEvaluationFormSchema'
import { MentalStatusExaminationFormSchema } from './MentalStatusExaminationFormSchema'

const mustBeTrue = (message: string) => z.boolean().refine((v) => v, { message })

export const INTAKE_NOTE_COMPREHENSIVE_FORM_SCHEMA = z
  .object({
    date: z.string(),
    FIRST_NAME: z.string().min(1),
    LAST_NAME: z.string().min(1),
    MEMBER_ID: z.string().min(1),
    CALL_START_TIME: z.string().min(1),
    CALL_END_TIME: z.string().min(1),
    PROVIDER_NAME: z.string().min(1),
    LICENSE_TYPE: z.string().min(1),

    SERVICE_DELIVERY_AND_COMPLIANCE: z.object({
      CONFIRM_SERVICE_PROVIDED_VIA_TELEHEALTH: mustBeTrue('Please confirm that service was provided via telehealth'),
      CONFIRM_INTAKE_TIME_AND_LOCATION: mustBeTrue('Please confirm that intake time and location were confirmed'),
      CONFIRM_SERVICE_PROVIDED_VIA_HIPAA_COMPLIANT_SOFTWARE: mustBeTrue(
        'Please confirm that service was provided via HIPAA compliant software'
      ),
    }),
    ACCESSIBILITY_NEEDS: AccessibilityNeedsFormSchema,
    REASON_FOR_SEEKING_TREATMENT: ReasonForSeekingTreatmentFormSchema,
    MENTAL_HEALTH_TREATMENT_HISTORY: MentalHealthTreatmentHistoryFormSchema,
    RISK_ASSESSMENT: RiskAssessmentFormSchema,
    BIOPYCHOSOCIAL_EVALUATION: BiopsychosocialEvaluationFormSchema,
    MENTAL_STATUS_EXAMINATION: MentalStatusExaminationFormSchema,

    BILLING_CODE: z.array(z.nativeEnum(BillingCode)).min(1, 'At least one billing code is required').default([]),
    CLINICAL_FORMULATION: z.string().min(1),
    CASE_CONCEPTUALIZATION: z.string().min(1),

    ELIGIBILITY: z.object({
      ELIGIBLE_FOR_PROGRAM_ENROLLMENT: z.boolean().optional(),
      NOT_ELIGIBLE_FOR_PROGRAM_ENROLLMENT: z.boolean().optional(),
      PARTICIPANT_DECLINES_ENROLLMENT: z.boolean().optional(),
    }),

    REVIEWED_CONFIDENTIALITY_AND_EXPECTATIONS: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (
      !data.ELIGIBILITY.ELIGIBLE_FOR_PROGRAM_ENROLLMENT &&
      !data.ELIGIBILITY.NOT_ELIGIBLE_FOR_PROGRAM_ENROLLMENT &&
      !data.ELIGIBILITY.PARTICIPANT_DECLINES_ENROLLMENT
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ELIGIBILITY'],
      })
    }

    if (!data.REVIEWED_CONFIDENTIALITY_AND_EXPECTATIONS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['REVIEWED_CONFIDENTIALITY_AND_EXPECTATIONS'],
        message: 'Reviewed confidentiality and expectations is required',
      })
    }
  })

export type IntakeNoteComprehensiveFormType = z.infer<typeof INTAKE_NOTE_COMPREHENSIVE_FORM_SCHEMA>
